// Bank details checks for Settings → Company Profile (2026-10-11).
//
// SJB's details were saved shifted one box (VAT number as sort code, sort code
// as account number), then a correction put the account number in the sort
// code box. These mirror the server rules (kitchenos-api modules/bank_details.js)
// so the retailer sees the problem under the field before saving; the server
// refuses the same shapes regardless.

export interface BankProblem {
  field: "bankSortCode" | "bankAccountNumber";
  message: string;
}

const digits = (v: string): string => v.replace(/[\s-]/g, "");

/** "040605" / "04 06 05" → "04-06-05"; anything that isn't 6 digits is returned as typed. */
export function formatSortCode(v: string): string {
  const d = digits(v.trim());
  return /^\d{6}$/.test(d) ? `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4, 6)}` : v.trim();
}

export function bankProblems(sortCode: string, accountNumber: string, vatNumber: string): BankProblem[] {
  const out: BankProblem[] = [];
  const sort = sortCode.trim();
  const acct = accountNumber.trim().replace(/\s/g, "");
  const vat = digits(vatNumber.trim()).replace(/^GB/i, "");
  if (sort) {
    if (vat && digits(sort) === vat) out.push({ field: "bankSortCode", message: "That is your VAT number, not your sort code." });
    else if (!/^\d{6}$/.test(digits(sort))) out.push({ field: "bankSortCode", message: "A sort code is 6 digits, like 12-34-56." });
  }
  if (acct) {
    if (/^\d{2}-\d{2}-\d{2}$/.test(acct)) {
      out.push({ field: "bankAccountNumber", message: `${acct} looks like a sort code. Put it under Sort code, and the account number here.` });
    } else if (vat && acct === vat) {
      out.push({ field: "bankAccountNumber", message: "That is your VAT number, not your account number." });
    } else if (!/^\d{6,10}$/.test(acct)) {
      out.push({ field: "bankAccountNumber", message: "An account number is digits only, usually 8." });
    } else if (sort && digits(sort) === acct) {
      out.push({ field: "bankAccountNumber", message: "The account number is the same as the sort code." });
    }
  }
  return out;
}

/** A server refusal that names a bank box ({ error, field }) → the problem to show beside it. */
export function serverBankProblem(raw: string): BankProblem | null {
  try {
    const j = JSON.parse(raw) as { error?: unknown; field?: unknown };
    if (j && typeof j.error === "string" && (j.field === "bankSortCode" || j.field === "bankAccountNumber")) {
      return { field: j.field, message: j.error };
    }
  } catch { /* not JSON */ }
  return null;
}

/** The server's 400 body is JSON ({ error }); show its message, not the raw text. */
export function serverErrorMessage(raw: string): string {
  try {
    const j = JSON.parse(raw) as { error?: unknown };
    if (j && typeof j.error === "string" && j.error) return j.error;
  } catch { /* not JSON */ }
  return raw;
}
